/**
 * BI > Produtividade por funcionário (#MT-D900606).
 * Separação + Conferência; início Separação = proxy; Conferência = checked_at (#MT-9C49478).
 */
import api from '../api.js';
import { notify } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import progress from '../../versa/progress.js';
import { formatBiHttpError } from './bi-http-error.js';

/** Máximo de linhas de detalhe renderizadas na prévia (evita travar o navegador). */
const PREVIEW_DETAIL_CAP = 80;
/** Alinhado à API: detalhe/prévia 31 dias; resumo 93. Evita 502 do nginx (timeout 300s). */
const MAX_DAYS_DETAIL = 31;
const MAX_DAYS_SUMMARY = 93;

function $(sel, root = document) {
	return root.querySelector(sel);
}

function defaultDateFirstOfMonth() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function defaultToday() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function yieldToMain() {
	return new Promise((resolve) => {
		requestAnimationFrame(() => setTimeout(resolve, 0));
	});
}

function setBtnState(btn, loading, label) {
	if (!btn) return;
	if (loading) {
		if (!btn.dataset.label) btn.dataset.label = btn.textContent;
		btn.textContent = label || 'Gerando…';
		btn.disabled = true;
	} else {
		btn.textContent = btn.dataset.label || btn.textContent;
		btn.disabled = false;
	}
}

function setModalBusy(root, busy) {
	const keys = ['Preview', 'ExportCsv', 'ExportSummary', 'Escape'];
	for (const k of keys) {
		const btn = root.querySelector(`.bn_${k}`);
		if (!btn) continue;
		if (busy && k !== 'Escape') setBtnState(btn, true, 'Gerando…');
		else if (!busy) setBtnState(btn, false);
	}
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch (e) {
		notify('Resposta de empresas inválida.', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

function parseIsoDate(s) {
	if (!s) return null;
	const d = new Date(`${s}T00:00:00`);
	return Number.isNaN(d.getTime()) ? null : d;
}

function inclusiveDays(fromStr, toStr) {
	const from = parseIsoDate(fromStr);
	if (!from) return 0;
	const to = parseIsoDate(toStr) || new Date();
	to.setHours(0, 0, 0, 0);
	from.setHours(0, 0, 0, 0);
	return Math.floor((to - from) / 86400000) + 1;
}

function maxDaysForView(view) {
	return view === 'summary' ? MAX_DAYS_SUMMARY : MAX_DAYS_DETAIL;
}

function windowError(fromStr, toStr, view) {
	if (!fromStr) return 'Informe a data inicial.';
	const days = inclusiveDays(fromStr, toStr);
	if (days < 1) return 'A data final não pode ser anterior à inicial.';
	const cap = maxDaysForView(view);
	if (days > cap) {
		const kind = view === 'summary' ? 'CSV resumo' : 'prévia e CSV detalhe';
		return `Período de ${days} dias excede o máximo de ${cap} dias para ${kind}. Reduza as datas ou use o CSV resumo (até ${MAX_DAYS_SUMMARY} dias).`;
	}
	return '';
}

function syncWindowGuard(root) {
	const df = $('#biop-date-from', root)?.value?.trim();
	const dt = $('#biop-date-to', root)?.value?.trim();
	const days = df ? inclusiveDays(df, dt) : 0;
	const hint = $('#biop-window-hint', root);
	const errDetail = windowError(df, dt, 'detail');
	const errSummary = windowError(df, dt, 'summary');
	if (hint) {
		if (!df) {
			hint.textContent = `Prévia e CSV detalhe: no máximo ${MAX_DAYS_DETAIL} dias. CSV resumo: até ${MAX_DAYS_SUMMARY} dias.`;
		} else if (errDetail && errSummary) {
			hint.textContent = errSummary;
		} else if (errDetail) {
			hint.textContent = `${days} dia(s) no filtro. ${errDetail}`;
		} else {
			hint.textContent = `${days} dia(s) no filtro (máx. ${MAX_DAYS_DETAIL} no detalhe / ${MAX_DAYS_SUMMARY} no resumo).`;
		}
	}
	const btnPrev = root.querySelector('.bn_Preview');
	const btnCsv = root.querySelector('.bn_ExportCsv');
	const btnSum = root.querySelector('.bn_ExportSummary');
	if (btnPrev) btnPrev.disabled = !df || !!errDetail;
	if (btnCsv) btnCsv.disabled = !df || !!errDetail;
	if (btnSum) btnSum.disabled = !df || !!errSummary;
}

function collectParams(node, format, view) {
	const companyId = $('#biop-company', node)?.value?.trim();
	if (!companyId) {
		notify('Selecione a empresa.');
		return null;
	}
	const df = $('#biop-date-from', node)?.value?.trim();
	if (!df) {
		notify('Informe a data inicial.');
		return null;
	}
	const dt = $('#biop-date-to', node)?.value?.trim();
	const winErr = windowError(df, dt, view === 'summary' ? 'summary' : 'detail');
	if (winErr) {
		notify(winErr, 'Produtividade');
		return null;
	}
	const sp = new URLSearchParams();
	sp.set('format', format);
	sp.set('date_from', df);
	if (dt) sp.set('date_to', dt);
	sp.set('activity', $('#biop-activity', node)?.value || 'both');
	if (view) sp.set('view', view);
	if ($('#biop-include-disabled', node)?.checked) sp.set('include_disabled', '1');
	const uid = $('#biop-user-id', node)?.value?.trim();
	if (uid) sp.set('user_id', uid);
	else {
		const us = $('#biop-user-search', node)?.value?.trim();
		if (us) sp.set('user_search', us);
	}
	const sid = $('#biop-output-id', node)?.value?.trim();
	if (sid) sp.set('output_id', sid);
	const oid = $('#biop-order-id', node)?.value?.trim();
	if (oid) sp.set('output_order_id', oid);
	const pid = $('#biop-product-id', node)?.value?.trim();
	if (pid) sp.set('product_id', pid);
	else {
		const ps = $('#biop-product-search', node)?.value?.trim();
		if (ps) sp.set('product_search', ps);
	}
	if (format === 'json') {
		sp.set('limit', $('#biop-limit', node)?.value || '200');
		sp.set('offset', $('#biop-offset', node)?.value || '0');
		sp.set('max_rows', $('#biop-max-rows', node)?.value || '50000');
	} else {
		const mx = $('#biop-max-rows', node)?.value?.trim();
		if (mx) sp.set('max_rows', mx);
	}
	return { companyId, sp };
}

function cellText(v) {
	if (v == null) return '';
	return String(v);
}

function fmtInd(v) {
	if (v == null || v === '') return '—';
	return cellText(v);
}

/** Prévia com textContent (innerHTML + template.innerHTML vinha vazio no Chrome). */
function renderPreviewTable(container, rows, title, opts = {}) {
	container.replaceChildren();
	const cap = opts.cap != null ? opts.cap : rows?.length;
	const slice = (rows || []).slice(0, cap);
	const total = rows?.length || 0;
	if (!slice.length) {
		const p = document.createElement('p');
		p.className = 'muted mts';
		p.textContent = `${title}: nenhuma linha.`;
		container.appendChild(p);
		return;
	}
	const keys = Object.keys(slice[0]);
	const section = document.createElement('div');
	section.className = 'biop-section mts';

	const head = document.createElement('div');
	head.className = 'biop-section-title';
	head.append(title, ' ');
	const count = document.createElement('span');
	count.className = 'biop-count';
	count.textContent = `(${slice.length}${total > slice.length ? ` de ${total}` : ''})`;
	head.appendChild(count);
	section.appendChild(head);

	if (opts.note) {
		const note = document.createElement('p');
		note.className = 'biop-hint';
		note.textContent = opts.note;
		section.appendChild(note);
	}

	const scroll = document.createElement('div');
	scroll.className = 'biop-scroll';
	const table = document.createElement('table');
	table.className = 'ibe-table';

	const thead = document.createElement('thead');
	const hr = document.createElement('tr');
	for (const k of keys) {
		const th = document.createElement('th');
		th.textContent = k;
		hr.appendChild(th);
	}
	thead.appendChild(hr);
	table.appendChild(thead);

	const tbody = document.createElement('tbody');
	const frag = document.createDocumentFragment();
	for (const r of slice) {
		const tr = document.createElement('tr');
		for (const k of keys) {
			const td = document.createElement('td');
			td.textContent = cellText(r[k]);
			tr.appendChild(td);
		}
		frag.appendChild(tr);
	}
	tbody.appendChild(frag);
	table.appendChild(tbody);
	scroll.appendChild(table);
	section.appendChild(scroll);
	container.appendChild(section);
}

function renderIndicators(container, ind, meta) {
	container.replaceChildren();
	const i = ind && typeof ind === 'object' ? ind : {};
	const wrap = document.createElement('div');
	wrap.className = 'biop-ind mts';

	const title = document.createElement('strong');
	title.textContent = 'Indicadores do período (filtro atual)';
	wrap.appendChild(title);

	const ul = document.createElement('ul');
	const items = [
		['Saídas (atividades)', fmtInd(i.n_saidas)],
		['Funcionários', fmtInd(i.n_funcionarios)],
		['Linhas SKU', fmtInd(i.n_linhas_sku)],
		['Volumes (soma)', fmtInd(i.soma_volumes)],
		['Qtd requerida / conferida', `${fmtInd(i.soma_qtd_requerida)} / ${fmtInd(i.soma_qtd_conferida)}`],
		['Tempo médio / pedido', fmtInd(i.tempo_medio_pedido)],
		['Tempo médio / linha SKU (proxy)', fmtInd(i.tempo_medio_sku)],
		['Itens/hora (qtd conferida ÷ horas)', fmtInd(i.itens_por_hora)],
	];
	for (const [label, value] of items) {
		const li = document.createElement('li');
		li.textContent = `${label}: `;
		const strong = document.createElement('strong');
		strong.textContent = value;
		li.appendChild(strong);
		ul.appendChild(li);
	}
	wrap.appendChild(ul);

	if (meta?.inicio_regra) {
		const hint = document.createElement('p');
		hint.className = 'biop-hint';
		hint.textContent = meta.inicio_regra;
		wrap.appendChild(hint);
	}
	container.appendChild(wrap);
}

function showPreviewLoading(indHost, summaryHost, previewHost) {
	indHost.replaceChildren();
	summaryHost.replaceChildren();
	previewHost.replaceChildren();
	const p = document.createElement('p');
	p.className = 'biop-loading muted mts';
	p.textContent = 'Gerando relatório… aguarde.';
	indHost.appendChild(p);
}

async function renderPreviewResults(indHost, summaryHost, previewHost, data, pg) {
	const meta = data.meta || {};
	const detail = data.detail || [];
	const summary = data.summary || [];
	const detailTotal = meta.detail_total ?? detail.length;
	const pageCount = detail.length;
	const showCount = Math.min(pageCount, PREVIEW_DETAIL_CAP);

	if (pg) pg.markStage('Montando indicadores…');
	renderIndicators(indHost, data.indicators, meta);
	await yieldToMain();

	if (pg) pg.markStage('Montando resumo por funcionário…');
	renderPreviewTable(summaryHost, summary, 'Resumo por funcionário');
	await yieldToMain();

	let detailNote = null;
	if (pageCount > showCount) {
		detailNote = `Exibindo ${showCount} de ${pageCount} linha(s) desta página (${detailTotal} no período). Use CSV detalhe para o conjunto completo.`;
	} else if (detailTotal > pageCount) {
		detailNote = `Página com ${pageCount} linha(s) de ${detailTotal} no período (ajuste offset/limite ou exporte CSV).`;
	}
	if (pg) pg.markStage('Montando amostra do detalhe…');
	renderPreviewTable(previewHost, detail, 'Detalhe (página)', {
		cap: PREVIEW_DETAIL_CAP,
		note: detailNote,
	});
	await yieldToMain();
}

function downloadCsv(blob, filename) {
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'bi-produtividade.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

export default async function bi_output_productivity() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.biop-hint { color: #555; font-size: 0.9rem; max-width: 52rem; }
			.biop-scroll {
				overflow: auto;
				max-height: 55vh;
				margin-top: 0.35rem;
				border: 1px solid var(--color-border, #cbd5e1);
				border-radius: 2px;
				background: #fff;
			}
			.biop-modal label { display: block; margin-top: 0.65rem; }
			.biop-modal .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; margin-top: 0.35rem; }
			.biop-modal .row > label { flex: 1 1 12rem; }
			.biop-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
			.biop-check { margin-top: 0.75rem; display: flex; align-items: center; gap: 0.5rem; }
			.biop-ind ul { margin: 0.35rem 0 0 1.1rem; }
			.biop-ind { max-width: 52rem; }
			.biop-section-title { font-weight: 600; font-size: 1rem; margin-bottom: 0.25rem; }
			.biop-count { font-weight: 400; color: #555; }
			.biop-loading { font-style: italic; }
		</style>
		<div class="title">Produtividade por funcionário</div>
		<p class="mts biop-hint">
			Relatório de <strong>Separação</strong> e/ou <strong>Conferência</strong> por período do
			<strong>fim da atividade</strong> (horário de Brasília). SKU, quantidades
			<strong>requerida</strong> / <strong>conferida</strong> e <strong>volumes</strong> da saída
			(como em Linhas de saída: valor do cabeçalho só na 1ª linha de cada saída).
			Na <strong>Separação</strong>, início é estimado (criação / primeira reserva) e o tempo é por saída
			(repetido nas linhas de produto — não é cronômetro por SKU).
			Na <strong>Conferência</strong>, início e fim são a <strong>data e hora em que a fatura foi conferida</strong>;
			o tempo gasto fica em branco (não inclui a espera desde a separação).
			Início real no coletor fica para fase futura.
		</p>
		<p class="mts"><button type="button" class="button" id="biop-open-modal">Filtros e geração</button></p>
		<div id="biop-indicators" class="mts"></div>
		<div id="biop-summary-host" class="mts"></div>
		<div id="biop-preview-host" class="mts"></div>
	`;

	const previewHost = $('#biop-preview-host', work);
	const summaryHost = $('#biop-summary-host', work);
	const indHost = $('#biop-indicators', work);

	const openModal = () => {
		const dlgHtml = `
<div class="msgbox biop-modal" style="min-width: min(40rem, 92vw); max-width: 96vw; text-align: left;">
	<div class="mb-title">Produtividade — filtros</div>
	<div class="mb-text" style="max-height: none;">
		<label>Empresa<sup>*</sup><select id="biop-company"></select></label>
		<div class="row">
			<label>Data inicial<sup>*</sup><input type="date" id="biop-date-from" required></label>
			<label>Data final<input type="date" id="biop-date-to" title="Vazio = até hoje (fuso SP)"></label>
			<label>Tipo de atividade
				<select id="biop-activity">
					<option value="both">Separação e Conferência</option>
					<option value="picking">Só Separação</option>
					<option value="conference">Só Conferência</option>
				</select>
			</label>
		</div>
		<div class="biop-check">
			<input type="checkbox" id="biop-include-disabled">
			<label for="biop-include-disabled" style="margin:0;">Incluir saídas excluídas (status 999)</label>
		</div>
		<div class="row">
			<label>Funcionário (UUID opcional)<input type="text" id="biop-user-id" placeholder="id do usuário" autocomplete="off"></label>
			<label>Busca funcionário (nome/login)<input type="text" id="biop-user-search" placeholder="se não informou UUID" autocomplete="off"></label>
		</div>
		<div class="row">
			<label>Nº saída (opcional)<input type="number" id="biop-output-id" min="1" step="1"></label>
			<label>Nº pedido saída (opcional)<input type="number" id="biop-order-id" min="1" step="1"></label>
		</div>
		<div class="row">
			<label>ID produto (opcional)<input type="number" id="biop-product-id" min="1" step="1"></label>
			<label>Busca produto (SKU ou nome)<input type="text" id="biop-product-search" autocomplete="off"></label>
		</div>
		<div class="row">
			<label>Prévia: limite<input type="number" id="biop-limit" value="200" min="1" max="5000" step="1"></label>
			<label>Prévia: offset<input type="number" id="biop-offset" value="0" min="0" step="1"></label>
			<label>Máx. linhas (resumo/CSV)<input type="number" id="biop-max-rows" value="50000" min="1" max="50000" step="1"></label>
		</div>
		<p class="biop-hint mts" id="biop-window-hint"></p>
		<p class="biop-hint mts">Prévia mostra indicadores + resumo por funcionário + amostra do detalhe (até ${PREVIEW_DETAIL_CAP} linhas na tela). O sistema <strong>não aceita</strong> período maior que o teto (evita falha 502).</p>
	</div>
	<div class="biop-actions">
		<button type="button" class="button bn_Preview">Prévia na tela</button>
		<button type="button" class="button bn_ExportCsv">CSV detalhe</button>
		<button type="button" class="button bn_ExportSummary">CSV resumo</button>
		<button type="button" class="button bn_Escape">Fechar</button>
	</div>
</div>`;

		const dlg = dialog.create(dlgHtml, async (key) => {
			const root = dlg.getNode();
			if (key === 'Escape') {
				dlg.destroy();
				return;
			}
			if (key !== 'Preview' && key !== 'ExportCsv' && key !== 'ExportSummary') return;

			const isPreview = key === 'Preview';
			const view = key === 'ExportSummary' ? 'summary' : 'detail';
			const pack = collectParams(root, isPreview ? 'json' : 'csv', isPreview ? null : view);
			if (!pack) return;

			const pg = progress({
				title: isPreview ? 'Gerando prévia do relatório…' : 'Gerando CSV…',
				text: isPreview
					? 'Consultando separação e conferência no servidor. Períodos grandes podem levar alguns instantes.'
					: 'O servidor está montando o arquivo. Aguarde o download.',
				indeterminate: true,
			});

			setModalBusy(root, true);
			if (isPreview) {
				dlg.destroy();
				showPreviewLoading(indHost, summaryHost, previewHost);
			}

			try {
				pg.markStage('Aguardando resposta do servidor…');
				if (typeof api.output_productivity_report !== 'function') {
					notify('Consulta de produtividade indisponível neste front. Recarregue a página (Ctrl+F5) após a atualização.', 'Produtividade');
					if (isPreview) indHost.replaceChildren();
					return;
				}
				const res = await api.output_productivity_report(pack.companyId, pack.sp.toString());

				if (isPreview) {
					const raw = await res.text();
					if (!res.ok) {
						notify(formatBiHttpError(res.status, raw), 'Produtividade');
						indHost.replaceChildren();
						return;
					}
					let data;
					try {
						data = JSON.parse(raw);
					} catch (e) {
						notify('Resposta não é JSON.', 'Produtividade');
						indHost.replaceChildren();
						return;
					}
					if (!data || typeof data !== 'object') {
						notify('Formato inesperado da API.', 'Produtividade');
						indHost.replaceChildren();
						return;
					}
					await renderPreviewResults(indHost, summaryHost, previewHost, data, pg);
					const n = (data.detail || []).length;
					const tot = data.meta?.detail_total ?? n;
					const shown = Math.min(n, PREVIEW_DETAIL_CAP);
					notify(
						`Prévia pronta: ${shown} linha(s) de detalhe na tela (${n} nesta página, ${tot} no período).`,
						'Produtividade',
					);
					return;
				}

				if (!res.ok) {
					const t = await res.text();
					notify(formatBiHttpError(res.status, t), 'Produtividade');
					return;
				}
				pg.markStage('Recebendo arquivo…');
				const blob = await res.blob();
				const cd = res.headers.get('Content-Disposition');
				let fname = view === 'summary' ? 'bi-produtividade-resumo.csv' : 'bi-produtividade-detalhe.csv';
				if (cd) {
					const m = cd.match(/filename="?([^";]+)"?/i);
					if (m) fname = m[1];
				}
				downloadCsv(blob, fname);
				dlg.destroy();
				notify('Download do CSV iniciado.', 'Produtividade');
			} catch (e) {
				console.error(e);
				notify(e?.message || String(e), 'Produtividade');
				if (isPreview) indHost.replaceChildren();
			} finally {
				pg.destroy();
				if (!isPreview) setModalBusy(root, false);
			}
		});

		const root = dlg.getNode();
		const df = $('#biop-date-from', root);
		const dt = $('#biop-date-to', root);
		if (df && !df.value) df.value = defaultDateFirstOfMonth();
		if (dt && !dt.value) dt.value = defaultToday();
		['biop-date-from', 'biop-date-to'].forEach((id) => {
			$(`#${id}`, root)?.addEventListener('change', () => syncWindowGuard(root));
			$(`#${id}`, root)?.addEventListener('input', () => syncWindowGuard(root));
		});
		syncWindowGuard(root);
		loadCompanies($('#biop-company', root)).then(() => {});
	};

	$('#biop-open-modal', work).addEventListener('click', openModal);
}
